import * as pdfjsLib from 'pdfjs-dist';

export interface PdfViewport {
  readonly width: number;
  readonly height: number;
}

export interface PdfRenderTask {
  readonly promise: Promise<unknown>;
}

// WHY: platform is consumed by both browser apps (admin, signer) and the Node-only api
// package. The api tsconfig does not include lib.dom, so referencing CanvasRenderingContext2D
// / HTMLCanvasElement here breaks tenant-wide typecheck. We expose minimal structural shapes;
// real DOM HTMLCanvasElement / CanvasRenderingContext2D are assignable to them.
export type PdfCanvasContext = object;

export interface PdfCanvasElement {
  width: number;
  height: number;
  getContext(contextId: '2d'): PdfCanvasContext | null;
}

export interface PdfPageHandle {
  getViewport(options: { scale: number }): PdfViewport;
  render(options: { canvasContext: PdfCanvasContext; viewport: PdfViewport }): PdfRenderTask;
}

export interface PdfDocumentHandle {
  readonly numPages: number;
  getPage(pageNumber: number): Promise<PdfPageHandle>;
}

export async function loadPdf(input: Uint8Array | ArrayBuffer): Promise<PdfDocumentHandle> {
  const data = input instanceof Uint8Array ? input : new Uint8Array(input);
  const loadingTask = pdfjsLib.getDocument({ data });
  const doc = await loadingTask.promise;
  return {
    numPages: doc.numPages,
    getPage: (pageNumber: number) => doc.getPage(pageNumber) as unknown as Promise<PdfPageHandle>,
  };
}

export async function renderPdfPageToCanvas(
  page: PdfPageHandle,
  canvas: PdfCanvasElement,
  scale: number
): Promise<void> {
  const viewport = page.getViewport({ scale });
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas 2d context unavailable');
  }
  const renderTask = page.render({ canvasContext: ctx, viewport });
  await renderTask.promise;
}

// WHY: pdfjs-dist resolves its worker URL relative to the running document by
// default, which fails in Vite-served SPAs (CDN fallback is blocked by CSP and
// the worker bundle must be served from the same origin). Each app MUST import
// the worker via `import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'`
// at bootstrap and call setPdfWorkerSrc(workerSrc) once.
export function setPdfWorkerSrc(url: string): void {
  pdfjsLib.GlobalWorkerOptions.workerSrc = url;
}
